
class CarouselManager {
  constructor(container) {
    this.container = container;
    this.currentIndex = 0;
    
    // 为每个实例生成唯一ID
    this.instanceId = `carousel-${Math.random().toString(36).substr(2, 9)}`;

    // 检测是否启用淡入淡出模式
    this.isFadeMode = container.classList.contains('fade-transition');

    // PC端元素
    this.navItems = container.querySelectorAll('.features-nav-item');
    this.indicator = container.querySelector('.carousel-nav-indicator');
    this.contentWrapper = container.querySelector('.features-content-wrapper');
    this.descriptionEl = container.querySelector('.carousel-description');

    // 滚动劫持相关属性
    this.isScrollLocked = false;           // 是否处于锁定状态
    this.scrollAccumulator = 0;            // 滚轮累积量
    this.wheelThreshold = 150;             // 切换阈值（px）- 增大以减慢切换速度
    this.lockedScrollPosition = 0;         // 锁定时的滚动位置
    this.wheelHandler = null;              // 保存wheel事件处理函数引用
    this.scrollHandler = null;             // 保存scroll事件处理函数引用
    this.intersectionObserver = null;      // 保存Observer引用
    this.totalSlides = this.getTotalSlides(); // 动态获取轮播图数量
    this.hasTriggered = false;             // 是否已经触发过（改名，每个实例独立）

    // 自动播放相关属性
    this.autoPlayTimer = null;             // 自动播放定时器
    this.autoPlayInterval = 7000;          // 自动播放间隔（7秒）

    // 检查是否有必要的PC端元素
    const hasPCElements = this.navItems.length && this.indicator && this.contentWrapper && this.descriptionEl;

    if (!hasPCElements) {
      console.warn('Carousel: Missing required PC elements in container:', container);
      return;
    }

    // 创建并插入移动端布局
    this.createMobileLayout();

    // 从PC端数据渲染移动端
    this.renderMobileFromPC();

    // 重新获取动态生成的移动端元素
    this.mobileCards = container.querySelectorAll('.mobile-card');
    this.mobileIndicators = container.querySelectorAll('.mobile-indicator');

    this.init();
    this.initMobile();

    // 初始化滚动劫持功能（仅PC端）
    // this.initScrollHijack();

    // 绑定窗口大小变化事件
    window.addEventListener('resize', () => this.handleResize());
  }

  // 创建移动端布局结构
  createMobileLayout() {
    // 创建移动端布局容器
    const mobileLayout = document.createElement('div');
    mobileLayout.className = 'features-section mobile-layout';
    mobileLayout.innerHTML = `
      <div class="mobile-carousel-wrapper"></div>
      <div class="mobile-indicators"></div>
      <div class="mobile-btn-box"></div>
    `;

    // 将移动端布局插入到PC端布局后面
    const pcLayout = this.container.querySelector('.pc-layout');
    if (pcLayout) {
      pcLayout.parentNode.insertBefore(mobileLayout, pcLayout.nextSibling);
    }

    // 更新移动端容器引用
    this.mobileWrapper = this.container.querySelector('.mobile-carousel-wrapper');
    this.mobileIndicatorsContainer = this.container.querySelector('.mobile-indicators');
    this.mobileBtnBox = this.container.querySelector('.mobile-btn-box');
  }

  // 从PC端数据渲染移动端
  renderMobileFromPC() {
    // 提取PC端数据
    const pcData = [];
    this.navItems.forEach((navItem, index) => {
      const title = navItem.textContent.trim();
      const description = navItem.getAttribute('data-description') || '';

      // 获取对应的图片
      const contentItem = this.contentWrapper.querySelector(`.features-content-item[data-index="${index}"]`);
      const img = contentItem ? contentItem.querySelector('img') : null;
      const imageSrc = img ? img.getAttribute('src') : '';
      const imageAlt = img ? img.getAttribute('alt') : title;

      pcData.push({
        title,
        description,
        imageSrc,
        imageAlt
      });
    });

    // 获取按钮链接
    const pcButton = this.container.querySelector('.pc-layout .features-btn');
    const buttonHref = pcButton ? pcButton.getAttribute('href') : '#';
    const buttonText = pcButton ? pcButton.textContent.trim() : 'Learn more';

    // 生成移动端卡片
    let mobileCardsHTML = '';
    pcData.forEach((data, index) => {
      mobileCardsHTML += `
        <div class="mobile-card" data-index="${index}">
          <div class="mobile-nav-title ${index === 0 ? 'active' : ''}">${data.title}</div>
          <div class="mobile-image">
            <img src="${data.imageSrc}" alt="${data.imageAlt}" width="375" height="200" loading="lazy" />
          </div>
          <div class="mobile-text">
            <p>${data.description}</p>
          </div>
        </div>
      `;
    });

    // 生成移动端指示器
    let mobileIndicatorsHTML = '';
    pcData.forEach((_, index) => {
      mobileIndicatorsHTML += `<div class="mobile-indicator ${index === 0 ? 'active' : ''}" data-index="${index}"></div>`;
    });

    // 生成移动端按钮
    const mobileBtnHTML = `<a href="${buttonHref}" class="features-btn">${buttonText}</a>`;

    // 插入到DOM中
    this.mobileWrapper.innerHTML = mobileCardsHTML;
    this.mobileIndicatorsContainer.innerHTML = mobileIndicatorsHTML;
    this.mobileBtnBox.innerHTML = mobileBtnHTML;
  }

  // 动态获取轮播图的数量
  getTotalSlides() {
    const contentItems = this.contentWrapper.querySelectorAll('.features-content-item');
    const navItems = this.navItems.length;
    
    // 使用content items和nav items中较小的数量，确保数据一致性
    const slideCount = Math.min(contentItems.length, navItems);
    
    console.log(`[${this.instanceId}] 检测到轮播图数量: ${slideCount} (content: ${contentItems.length}, nav: ${navItems})`);
    
    return slideCount > 0 ? slideCount : 1; // 至少返回1，避免除0错误
  }

  init() {
    // 设置初始状态
    this.updateIndicatorPosition(0);
    this.updateContent(0);

    // 淡入淡出模式下初始化active状态
    if (this.isFadeMode) {
      this.initFadeMode();
    }

    // 确保在DOM完全渲染后重新计算指示器位置
    setTimeout(() => {
      this.updateIndicatorPosition(0);
    }, 100);

    // 绑定导航点击事件
    this.navItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        if (index === this.currentIndex) return;

        // 移除所有激活状态
        this.navItems.forEach(nav => nav.classList.remove('active'));

        // 设置当前激活状态
        item.classList.add('active');

        // 更新当前索引
        this.currentIndex = index;

        // 更新指示器位置和内容
        this.updateIndicatorPosition(index);
        this.updateContent(index);

        // 用户手动切换时，重置自动播放定时器
        this.resetAutoPlay();
      });
    });

    // 启动自动播放（仅PC端）
    this.startAutoPlay();
  }

  updateIndicatorPosition(index) {
    if (!this.indicator || !this.navItems.length) return;

    const navItem = this.navItems[index];
    const navContainer = navItem.parentElement;

    // 计算位置（相对于padding区域内）
    const containerRect = navContainer.getBoundingClientRect();
    const itemRect = navItem.getBoundingClientRect();

    // 获取容器的padding
    const containerStyle = window.getComputedStyle(navContainer);
    const paddingLeft = parseFloat(containerStyle.paddingLeft) || 0;

    // 计算相对于padding区域内的位置
    const left = itemRect.left - containerRect.left - paddingLeft;
    const width = itemRect.width;

    // 步骤1: 移动指示器到新位置，并将宽度重置为0（使用快速过渡）
    this.indicator.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    this.indicator.style.transform = `translateX(${left}px)`;
    this.indicator.style.width = '0px';

    // 步骤2: 在下一帧启动7秒的宽度增长动画
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.indicator.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 7s linear';
        this.indicator.style.width = `${width}px`;
      });
    });
  }

  updateContent(index) {
    if (!this.contentWrapper || !this.descriptionEl) return;

    // 检测是否为移动端
    const isMobile = window.innerWidth <= 768;

    // PC端：检查是否启用淡入淡出模式
    if (!isMobile && this.isFadeMode) {
      this.updateFadeContent(index);
    } else {
      // 原有的滑动模式
      let itemWidth, gap, translateX;

      if (isMobile) {
        // 移动端计算
        itemWidth = window.innerWidth - 60; // calc(100vw - 60px)
        gap = 32;
        translateX = -index * (itemWidth + gap);
      } else {
        // PC端计算
        itemWidth = 1200;
        gap = 48;
        translateX = -index * (itemWidth + gap);
      }

      this.contentWrapper.style.transform = `translateX(${translateX}px)`;

      // 更新容器宽度（基于实际item数量）
      if (!isMobile) {
        const itemCount = this.contentWrapper.querySelectorAll('.features-content-item').length;
        const totalWidth = itemCount * itemWidth + (itemCount - 1) * gap;
        this.contentWrapper.style.width = `${totalWidth}px`;
      }
    }

    // 从当前导航项获取描述文字
    const currentNavItem = this.navItems[index];
    const description = currentNavItem.getAttribute('data-description');
    if (description) {
      this.descriptionEl.textContent = description;
    }

    // 移动端导航栏滚动到当前项
    if (isMobile) {
      this.scrollNavToActive(index);
    }
  }

  scrollNavToActive(index) {
    const navContainer = this.navItems[0].parentElement;
    const activeNavItem = this.navItems[index];

    if (!navContainer || !activeNavItem) return;

    // 计算滚动位置，让当前项居中显示
    const containerWidth = navContainer.clientWidth;
    const itemOffsetLeft = activeNavItem.offsetLeft;
    const itemWidth = activeNavItem.offsetWidth;

    const scrollLeft = itemOffsetLeft - (containerWidth / 2) + (itemWidth / 2);

    navContainer.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  }

  // 初始化移动端轮播
  initMobile() {
    if (!this.mobileWrapper || !this.mobileCards.length || !this.mobileIndicators.length) return;

    // 设置初始状态
    this.updateMobileCarousel(0);

    // 绑定指示器点击事件
    this.mobileIndicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => {
        if (index === this.currentIndex) return;
        this.currentIndex = index;
        this.updateMobileCarousel(index);
        this.updateMobileIndicators(index);
      });
    });

    // 绑定触摸滑动事件
    this.initTouchEvents();
  }

  // 更新移动端轮播位置
  updateMobileCarousel(index) {
    if (!this.mobileWrapper) return;

    // 每次移动(卡片宽度 + gap间距)的距离
    const cardWidth = window.innerWidth - 48; // 390px - 48px = 342px
    const gap = 12;
    const moveDistance = cardWidth + gap; // 342px + 12px = 354px
    const translateX = -index * moveDistance;

    this.mobileWrapper.style.transform = `translateX(${translateX}px)`;
  }

  // 更新移动端指示器
  updateMobileIndicators(index) {
    this.mobileIndicators.forEach((indicator, i) => {
      indicator.classList.toggle('active', i === index);
    });

    // 更新移动端nav标题的active状态
    const mobileNavTitles = this.container.querySelectorAll('.mobile-nav-title');
    mobileNavTitles.forEach((title, i) => {
      title.classList.toggle('active', i === index);
    });
  }

  // 初始化触摸事件
  initTouchEvents() {
    if (!this.mobileWrapper) return;

    let startX = 0;
    let startY = 0;
    let isDragging = false;
    let startTime = 0;

    this.mobileWrapper.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
      startTime = Date.now();
      this.mobileWrapper.style.transition = 'none';
    }, { passive: true });

    this.mobileWrapper.addEventListener('touchmove', (e) => {
      if (!isDragging) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = currentX - startX;
      const diffY = currentY - startY;

      // 垂直滑动时不处理
      if (Math.abs(diffY) > Math.abs(diffX)) {
        return;
      }

      e.preventDefault();

      const cardWidth = window.innerWidth - 48;
      const gap = 12;
      const moveDistance = cardWidth + gap;
      const currentTranslateX = -this.currentIndex * moveDistance;
      const newTranslateX = currentTranslateX + diffX;

      this.mobileWrapper.style.transform = `translateX(${newTranslateX}px)`;
    }, { passive: false });

    this.mobileWrapper.addEventListener('touchend', (e) => {
      if (!isDragging) return;

      isDragging = false;
      this.mobileWrapper.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

      const endX = e.changedTouches[0].clientX;
      const diffX = endX - startX;
      const diffTime = Date.now() - startTime;

      // 快速滑动或滑动距离超过阈值
      const threshold = 50;
      const isQuickSwipe = diffTime < 300 && Math.abs(diffX) > 30;
      const isLongSwipe = Math.abs(diffX) > threshold;

      if (isQuickSwipe || isLongSwipe) {
        if (diffX > 0 && this.currentIndex > 0) {
          // 向右滑动，上一张
          this.currentIndex--;
        } else if (diffX < 0 && this.currentIndex < this.mobileCards.length - 1) {
          // 向左滑动，下一张
          this.currentIndex++;
        }
      }

      this.updateMobileCarousel(this.currentIndex);
      this.updateMobileIndicators(this.currentIndex);
    }, { passive: true });
  }

  // 监听窗口大小变化，重新计算布局
  handleResize() {
    this.updateContent(this.currentIndex);
    this.updateIndicatorPosition(this.currentIndex);
    this.updateMobileCarousel(this.currentIndex);

    // 淡入淡出模式下重新计算容器高度
    if (this.isFadeMode) {
      this.setFadeContainerHeight();
    }

    // 处理滚动劫持：如果变成移动端，清理；如果变成PC端，重新初始化
    const isMobile = window.innerWidth <= 768;
    if (isMobile && this.intersectionObserver) {
      this.cleanupScrollHijack();
    } else if (!isMobile && !this.intersectionObserver) {
      // this.initScrollHijack(); // 已注释掉滚动劫持功能
    }

    // 处理自动播放：如果变成移动端，停止自动播放；如果变成PC端，启动自动播放
    if (isMobile) {
      this.stopAutoPlay();
    } else if (!this.autoPlayTimer) {
      this.startAutoPlay();
    }
  }

  // 初始化淡入淡出模式
  initFadeMode() {
    // 只在PC端使用淡入淡出：给第一个item添加active类
    const contentItems = this.contentWrapper.querySelectorAll('.features-content-item');
    contentItems.forEach((item, index) => {
      item.classList.toggle('active', index === 0);
    });

    // 设置容器高度为最高图片的高度
    this.setFadeContainerHeight();
  }

  // PC端淡入淡出切换
  updateFadeContent(index) {
    const contentItems = this.contentWrapper.querySelectorAll('.features-content-item');

    // 移除所有active状态
    contentItems.forEach(item => {
      item.classList.remove('active');
    });

    // 给目标item添加active状态
    if (contentItems[index]) {
      contentItems[index].classList.add('active');
    }
  }

  // 设置淡入淡出模式容器高度
  setFadeContainerHeight() {
    if (!this.isFadeMode || !this.contentWrapper) return;

    const contentItems = this.contentWrapper.querySelectorAll('.features-content-item');
    let maxHeight = 0;

    // 临时显示所有items来测量高度
    contentItems.forEach(item => {
      const originalPosition = item.style.position;
      const originalOpacity = item.style.opacity;

      item.style.position = 'static';
      item.style.opacity = '1';

      const height = item.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }

      // 恢复原始样式
      item.style.position = originalPosition;
      item.style.opacity = originalOpacity;
    });

    // 设置容器高度
    if (maxHeight > 0) {
      this.contentWrapper.style.height = `${maxHeight}px`;
    }
  }

  // ========== 滚动劫持功能 ==========

  // 初始化滚动劫持（仅PC端）
  initScrollHijack() {
    // 检测是否为移动端
    const isMobile = window.innerWidth <= 768;
    if (isMobile) return;

    console.log(`[${this.instanceId}] 初始化滚动劫持`);
    
    // 找到 features-content 元素（轮播图内容区域）
    const featuresContent = this.container.querySelector('.features-content');
    if (!featuresContent) {
      console.log(`[${this.instanceId}] 找不到 .features-content 元素`);
      return;
    }
    
    this.featuresContent = featuresContent;
    
    // 使用滚动事件持续监听位置
    this.scrollCheckHandler = () => {
      // 如果已经锁定，不再检查
      if (this.isScrollLocked) return;
      
      const rect = this.featuresContent.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const distance = Math.abs(elementCenter - viewportCenter);
      
      // 检查元素是否在视口内
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
      // 更严格的中心判断：距离中心小于50px才触发
      const isInCenter = distance < 50;
      
      // 每次滚动都检查（但只在接近时输出日志避免刷屏）
      if (isInViewport && distance < 150) {
        console.log(`[${this.instanceId}] 滚动检查:`, {
          elementCenter: elementCenter.toFixed(0),
          viewportCenter: viewportCenter.toFixed(0),
          distance: distance.toFixed(0),
          isInCenter,
          hasTriggered: this.hasTriggered
        });
      }
      
      // 触发条件：在视口内 + 非常接近中心 + 未触发过
      if (isInViewport && isInCenter && !this.hasTriggered) {
        console.log(`[${this.instanceId}] 轮播图到达屏幕中心，触发锁定！`);
        this.lockScroll();
      }
      
      // 重置条件：元素完全离开视口
      if (!isInViewport && this.hasTriggered && !this.isScrollLocked) {
        console.log(`[${this.instanceId}] 元素离开视口，重置状态`);
        this.hasTriggered = false;
      }
    };
    
    // 监听滚动事件，使用节流避免性能问题
    let ticking = false;
    this.throttledScrollCheck = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.scrollCheckHandler();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', this.throttledScrollCheck, { passive: true });
  }

  // 锁定页面滚动
  lockScroll() {
    if (this.isScrollLocked) return;

    console.log(`[${this.instanceId}] 🔒 锁定滚动`);
    
    this.isScrollLocked = true;
    this.scrollAccumulator = 0;
    this.hasTriggered = true;
    this.lockedScrollPosition = window.scrollY;

    // 重置到第一张
    this.currentIndex = 0;
    this.updateContent(0);
    this.updateIndicatorPosition(0);
    this.navItems.forEach((nav, index) => nav.classList.toggle('active', index === 0));

    // 获取滚动条宽度
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // 锁定滚动但保持滚动条空间，避免页面跳动
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // 监听滚轮事件
    this.wheelHandler = (e) => {
      e.preventDefault();
      
      // 累积滚动量
      this.scrollAccumulator += Math.abs(e.deltaY);
      
      // 阈值：触控板250，鼠标150
      const threshold = Math.abs(e.deltaY) < 50 ? 250 : 150;
      
      if (this.scrollAccumulator >= threshold) {
        this.scrollAccumulator = 0;
        
        if (e.deltaY > 0) {
          // 向下滚
          if (this.currentIndex < this.totalSlides - 1) {
            this.currentIndex++;
            this.switchSlide(this.currentIndex);
            console.log(`[${this.instanceId}] → 第${this.currentIndex + 1}张`);
          } else {
            // 最后一张，解锁
            console.log(`[${this.instanceId}] 完成，解锁`);
            this.unlockScroll();
            setTimeout(() => window.scrollBy({ top: 100, behavior: 'smooth' }), 50);
          }
        } else {
          // 向上滚
          if (this.currentIndex > 0) {
            this.currentIndex--;
            this.switchSlide(this.currentIndex);
            console.log(`[${this.instanceId}] ← 第${this.currentIndex + 1}张`);
          } else {
            // 第一张，解锁
            console.log(`[${this.instanceId}] 回到开始，解锁`);
            this.unlockScroll();
            setTimeout(() => window.scrollBy({ top: -100, behavior: 'smooth' }), 50);
          }
        }
      }
    };
    
    window.addEventListener('wheel', this.wheelHandler, { passive: false });
  }

  // 解锁页面滚动
  unlockScroll() {
    if (!this.isScrollLocked) return;

    console.log(`[${this.instanceId}] 🔓 解锁`);
    
    this.isScrollLocked = false;

    if (this.wheelHandler) {
      window.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }

    // 恢复样式，移除padding补偿
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  // 切换轮播图
  switchSlide(index) {
    this.updateContent(index);
    this.updateIndicatorPosition(index);
    this.navItems.forEach((nav, i) => nav.classList.toggle('active', i === index));
  }

  // 清理滚动劫持
  cleanupScrollHijack() {
    if (this.isScrollLocked) this.unlockScroll();

    if (this.throttledScrollCheck) {
      window.removeEventListener('scroll', this.throttledScrollCheck);
      this.throttledScrollCheck = null;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    this.hasTriggered = false;
  }

  // ========== 自动播放功能 ==========

  // 启动自动播放（仅PC端）
  startAutoPlay() {
    // 检测是否为移动端
    const isMobile = window.innerWidth <= 768;
    if (isMobile) return;

    // 如果已有定时器，先清除
    this.stopAutoPlay();

    console.log(`[${this.instanceId}] 🔄 启动自动播放 (每${this.autoPlayInterval / 1000}秒)`);

    this.autoPlayTimer = setInterval(() => {
      // 计算下一个索引
      const nextIndex = (this.currentIndex + 1) % this.totalSlides;

      console.log(`[${this.instanceId}] 🔄 自动切换: ${this.currentIndex} → ${nextIndex}`);

      // 更新当前索引
      this.currentIndex = nextIndex;

      // 更新导航项激活状态
      this.navItems.forEach((nav, i) => nav.classList.toggle('active', i === nextIndex));

      // 更新指示器位置和内容
      this.updateIndicatorPosition(nextIndex);
      this.updateContent(nextIndex);
    }, this.autoPlayInterval);
  }

  // 停止自动播放
  stopAutoPlay() {
    if (this.autoPlayTimer) {
      console.log(`[${this.instanceId}] ⏸️  停止自动播放`);
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
  }

  // 重置自动播放（用于用户交互后重新开始计时）
  resetAutoPlay() {
    console.log(`[${this.instanceId}] 🔁 重置自动播放`);
    this.stopAutoPlay();
    this.startAutoPlay();
  }
}
// 自动初始化所有轮播图
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('.features-section-box');
  console.log(`🚀 找到 ${containers.length} 个轮播图容器，开始初始化...`);
  
  containers.forEach((container, index) => {
    console.log(`📦 初始化第 ${index + 1} 个轮播图`);
    new CarouselManager(container);
  });
  
  console.log('✅ 所有轮播图初始化完成');
});